import "./app/styles.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./app/app";
import { router } from "./app/router";
import { installStaleBuildGuard } from "./shared/navigation/stale-build-guard";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const root = rootElement;

function ensureDevRandomUuid(): void {
  const maybeCrypto = globalThis.crypto as Crypto | undefined;
  if (typeof maybeCrypto?.randomUUID === "function") {
    return;
  }

  // Non-secure contexts (e.g. http://<wsl-ip>) omit randomUUID; mocks only need
  // opaque session ids, so fall back to getRandomValues when available.
  const cryptoHost = maybeCrypto;
  if (!cryptoHost?.getRandomValues) {
    throw new Error("Secure random source unavailable in this browser context.");
  }

  Object.defineProperty(cryptoHost, "randomUUID", {
    configurable: true,
    value: () => {
      const bytes = new Uint8Array(16);
      cryptoHost.getRandomValues(bytes);
      const versionByte = bytes[6] ?? 0;
      const clockByte = bytes[8] ?? 0;
      bytes[6] = (versionByte & 0x0f) | 0x40;
      bytes[8] = (clockByte & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    },
  });
}

async function bootstrap(): Promise<void> {
  if (import.meta.env.DEV) {
    ensureDevRandomUuid();
    const { startMocks } = await import("./mocks/start");

    await startMocks();
  }

  if (import.meta.env.PROD) {
    installStaleBuildGuard(router);
  }

  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap().catch((error: unknown) => {
  console.error("Application bootstrap failed", error);

  const message = document.createElement("p");
  message.setAttribute("role", "alert");
  message.textContent =
    "Não foi possível iniciar o ambiente de desenvolvimento com segurança.";
  root.replaceChildren(message);
});
